import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { FormAutocomplete } from '~/components/FormComponents/FormAutocomplete.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import {
  GuaranteeSecurityType,
  type TGuaranteesExecutionForm,
  type TPatchGuaranteesExecutionFormPayload,
} from '~/store/guarantees/types.ts';
import FlowSection from '../../components/root/shared/FlowSection.tsx';
import {
  ACCOUNT_PLACEMENT_STATUS_OPTIONS,
  EXECUTOR_MAIN_DEAL_CONDITIONS_STATUS_OPTIONS,
  UAKDO_CONTROL_TEXT,
} from './constants.ts';
import { uakdoControlSectionStyles } from './styles.ts';
import {
  GuaranteesUakdoControlFieldName,
  type TGuaranteesUakdoControlFormValues,
} from './types.ts';
import {
  buildGuaranteesUakdoControlPayload,
  createGuaranteesUakdoControlResolver,
  getGuaranteesUakdoControlInitialValues,
} from './utils.ts';

type TUakdoControlSectionProps = {
  executionForm: TGuaranteesExecutionForm | null;
  isConfirming: boolean;
  isDisabled?: boolean;
  isLoading: boolean;
  isSaving: boolean;
  securityType: GuaranteeSecurityType | '';
  onConfirm: (payload: TPatchGuaranteesExecutionFormPayload) => Promise<void>;
  onSubmit: (payload: TPatchGuaranteesExecutionFormPayload) => Promise<unknown>;
};

const UakdoControlSection: React.FC<TUakdoControlSectionProps> = ({
  executionForm,
  isConfirming,
  isDisabled = false,
  isLoading,
  isSaving,
  securityType,
  onConfirm,
  onSubmit,
}) => {
  const defaultValues = React.useMemo(
    () => getGuaranteesUakdoControlInitialValues(executionForm),
    [executionForm]
  );
  const resolver = React.useMemo(
    () => createGuaranteesUakdoControlResolver(securityType),
    [securityType]
  );
  const formMethods = useForm<TGuaranteesUakdoControlFormValues>({
    defaultValues,
    resolver,
    mode: 'onSubmit',
  });
  const {
    formState: { isDirty },
    handleSubmit,
    reset,
  } = formMethods;
  const areFieldsDisabled = isSaving || isConfirming || isDisabled;
  const isSaveDisabled = areFieldsDisabled || !isDirty;
  const saveStartIcon = isSaving ? <ButtonLoadingIcon /> : undefined;
  const confirmStartIcon = isConfirming ? <ButtonLoadingIcon /> : undefined;
  const showCoverageStatus = securityType === GuaranteeSecurityType.COVERAGE_ACCOUNT;
  const showDepositStatus = securityType === GuaranteeSecurityType.DEPOSIT_RIGHTS;

  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleSave: SubmitHandler<TGuaranteesUakdoControlFormValues> = async (values) => {
    try {
      await onSubmit(buildGuaranteesUakdoControlPayload(values, securityType));
      reset(values);
    } catch {
      // Request feedback is handled by the page-level callback.
    }
  };

  const handleConfirm: SubmitHandler<TGuaranteesUakdoControlFormValues> = async (values) => {
    try {
      await onConfirm(buildGuaranteesUakdoControlPayload(values, securityType));
      reset(values);
    } catch {
      // Request feedback is handled by the page-level callback.
    }
  };

  if (isLoading) {
    return (
      <FlowSection
        step={UAKDO_CONTROL_TEXT.sectionStepLabel}
        title={UAKDO_CONTROL_TEXT.sectionTitle}
        description={UAKDO_CONTROL_TEXT.sectionDescription}
      >
        <Box sx={guaranteesClientPageStyles.flowLoadingState}>
          <CircularProgress size={24} />
          <Typography sx={guaranteesClientPageStyles.flowLockHint}>
            {UAKDO_CONTROL_TEXT.loadingText}
          </Typography>
        </Box>
      </FlowSection>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <FlowSection
        step={UAKDO_CONTROL_TEXT.sectionStepLabel}
        title={UAKDO_CONTROL_TEXT.sectionTitle}
        description={UAKDO_CONTROL_TEXT.sectionDescription}
      >
        <Box
          component="form"
          onSubmit={(event) => {
            void handleSubmit(handleSave)(event);
          }}
        >
          <Box sx={uakdoControlSectionStyles.fieldsStack}>
            <Box sx={uakdoControlSectionStyles.topRow}>
              <FormAutocomplete
                name={GuaranteesUakdoControlFieldName.ExecutorMainDealConditionsStatus}
                label={UAKDO_CONTROL_TEXT.executorMainDealConditionsStatusLabel}
                placeholder={UAKDO_CONTROL_TEXT.executorMainDealConditionsStatusPlaceholder}
                options={EXECUTOR_MAIN_DEAL_CONDITIONS_STATUS_OPTIONS}
                disabled={areFieldsDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              {showCoverageStatus && (
                <FormAutocomplete
                  name={GuaranteesUakdoControlFieldName.CoverageAccountPlacementStatus}
                  label={UAKDO_CONTROL_TEXT.coverageAccountPlacementStatusLabel}
                  placeholder={UAKDO_CONTROL_TEXT.coverageAccountPlacementStatusPlaceholder}
                  options={ACCOUNT_PLACEMENT_STATUS_OPTIONS}
                  disabled={areFieldsDisabled}
                  sx={guaranteesClientPageStyles.flowField}
                />
              )}
              {showDepositStatus && (
                <FormAutocomplete
                  name={GuaranteesUakdoControlFieldName.DepositAccountPlacementStatus}
                  label={UAKDO_CONTROL_TEXT.depositAccountPlacementStatusLabel}
                  placeholder={UAKDO_CONTROL_TEXT.depositAccountPlacementStatusPlaceholder}
                  options={ACCOUNT_PLACEMENT_STATUS_OPTIONS}
                  disabled={areFieldsDisabled}
                  sx={guaranteesClientPageStyles.flowField}
                />
              )}
            </Box>
            <FormInput
              name={GuaranteesUakdoControlFieldName.UnfulfilledConditionsDetails}
              label={UAKDO_CONTROL_TEXT.unfulfilledConditionsDetailsLabel}
              disabled={areFieldsDisabled}
              multiline
              minRows={3}
              sx={uakdoControlSectionStyles.fullWidthField}
            />
          </Box>

          <Box sx={guaranteesClientPageStyles.flowActions}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSaveDisabled}
              startIcon={saveStartIcon}
              sx={dataFormStyles.documentPrimaryAction}
            >
              {UAKDO_CONTROL_TEXT.saveButton}
            </Button>
          </Box>
        </Box>
      </FlowSection>

      <Box sx={guaranteesClientPageStyles.flowPageActionsCentered}>
        <Button
          type="button"
          variant="contained"
          disabled={areFieldsDisabled}
          onClick={() => void handleSubmit(handleConfirm)()}
          startIcon={confirmStartIcon}
          sx={dataFormStyles.documentPrimaryAction}
        >
          {UAKDO_CONTROL_TEXT.submitButton}
        </Button>
      </Box>
    </FormProvider>
  );
};

export default UakdoControlSection;
